import React, { useState, useEffect, useRef } from 'react'
import {
  Modal,
  Form,
  Select,
  Input,
  InputNumber,
  Button,
  Row,
  Col,
  Alert,
} from 'antd'
import moment from 'moment'
import MultiCauseCodePropmt from '../../card-data/MultiCauseCode'
import { DERATE_TYPES, OUTAGE_TYPES, ZERO_CAP_TYPES } from './drlUtils'

const { Option } = Select
const { TextArea } = Input

const HOURS = Array.from({ length: 24 }, (_, i) => i + 1)
const MINUTES = Array.from({ length: 60 }, (_, i) => i)
const LOCKED_STATUSES = new Set(['S', 'L', 'R'])

const EventModal = ({
  visible,
  onCancel,
  onSave,
  form,
  editData,
  initialEventStart,
  selectedDate,
  existingEvents,
  selectedUnit,
  eventTypes,
  contribConditions,
  causeCodeList,
  unitHourlyRows,
  unitType = [],
  saveError,
  saveLoading,
}) => {
  const { getFieldDecorator } = form
  const formRef = useRef(form)

  const [causeCodeSearchVisible, setCauseCodeSearchVisible] = useState(false)
  const [selectedCauseCode, setSelectedCauseCode] = useState(null)
  const [causeCodeKeyValues, setCauseCodeKeyValues] = useState(null)
  const [causeCodeValues, setCauseCodeValues] = useState(null)
  const [eventTypeValue, setEventTypeValue] = useState(null)
  const [derate, setDerate] = useState(null)

  const isRS = eventTypeValue === 'RS'
  const isZeroCap = ZERO_CAP_TYPES.has(eventTypeValue)
  const isEdit = !!editData
  const useDerate = !isEdit && !isZeroCap

  useEffect(() => {
    formRef.current = form
  }, [form])

  useEffect(() => {
    if (!visible) return
    const f = formRef.current
    if (editData) {
      const editMoment = editData.eventStart
        ? moment(editData.eventStart, 'MM/DD/YYYY HH:mm')
        : null
      f.setFieldsValue({
        eventType: editData.eventType || undefined,
        eventHour: editMoment ? editMoment.hour() + 1 : 1,
        eventMinute: editMoment ? editMoment.minute() : 0,
        availCap: editData.availCap != null ? editData.availCap : undefined,
        causeCode: editData.causeCode ? String(editData.causeCode) : undefined,
        coc: editData.coc || '0',
        description: editData.description || undefined,
      })
      if (editData.causeCode)
        setSelectedCauseCode({ causeCode: editData.causeCode, description: '' })
    } else {
      f.resetFields()
      setEventTypeValue(null)
      setDerate(null)
      if (initialEventStart) {
        f.setFieldsValue({
          eventHour: initialEventStart.hour() + 1,
          eventMinute: initialEventStart.minute() || 0,
        })
      }
      setSelectedCauseCode(null)
      setCauseCodeKeyValues(null)
      setCauseCodeValues(null)
    }
  }, [visible, editData, initialEventStart])

  useEffect(() => {
    if (!isRS) return
    formRef.current.setFieldsValue({
      availCap: 0,
      causeCode: '0',
      description: 'Reserve Shutdown',
    })
    setSelectedCauseCode({ causeCode: '0', description: '' })
  }, [isRS])

  // Auto-set availCap=0 for unplanned/outage types (causeCode/description stay user-editable)
  useEffect(() => {
    if (!isZeroCap || isRS) return
    formRef.current.setFieldsValue({ availCap: 0 })
  }, [isZeroCap, isRS])

  const handleSave = () => {
    if (!isZeroCap && unallocForHour != null) {
      const cap = form.getFieldValue('availCap')
      if (cap != null && cap > unallocForHour) {
        Modal.error({
          title: 'AvailCap Exceeds Unallocated',
          content: `AvailCap ${cap} exceeds Unallocated ${unallocForHour.toFixed(
            1
          )} for H${
            constrainingHour != null
              ? String(constrainingHour).padStart(2, '0')
              : '--'
          }`,
          zIndex: 1100,
        })
        return
      }
    }
    form.validateFields((err, values) => {
      if (err) return

      if (!editData && existingEvents && existingEvents.length > 0) {
        const causeCodeToCheck = selectedCauseCode
          ? String(selectedCauseCode.causeCode)
          : String(values.causeCode || '')
        const duplicate = existingEvents.find(
          e =>
            String(e.eventType) === String(values.eventType) &&
            String(e.causeCode) === causeCodeToCheck
        )
        if (duplicate) {
          Modal.error({
            title: 'Duplicate Event',
            content: `An event with type "${values.eventType}" and cause code "${causeCodeToCheck}" already exists for this unit.`,
            zIndex: 1100,
          })
          return
        }
      }

      const h = values.eventHour != null ? values.eventHour - 1 : 0
      const m = values.eventMinute != null ? values.eventMinute : 0
      const base = selectedDate ? selectedDate.clone() : moment()
      const eventStart = base
        .clone()
        .hour(h)
        .minute(m)
        .second(0)
        .format('MM/DD/YYYY HH:mm')

      onSave({
        ...values,
        eventStart,
        availCap: ZERO_CAP_TYPES.has(values.eventType) ? 0 : values.availCap,
        causeCode: selectedCauseCode
          ? selectedCauseCode.causeCode
          : values.causeCode,
      })
      form.resetFields()
      setSelectedCauseCode(null)
      setCauseCodeKeyValues(null)
      setCauseCodeValues(null)
    })
  }

  const onCauseCodeDialogClose = (keys, values) => {
    setCauseCodeSearchVisible(false)
    setCauseCodeKeyValues(keys)
    setCauseCodeValues(values)
    if (values && values.length > 0) {
      const cc = values[0]
      setSelectedCauseCode(cc)
      form.setFieldsValue({ causeCode: String(cc.causeCode) })
    }
  }

  const onCauseCodeTreeCancel = keys => {
    setCauseCodeSearchVisible(false)
    setCauseCodeKeyValues(keys)
  }

  // Cause code description from typed number
  const causeCodeInputVal = form.getFieldValue('causeCode')
  const causeCodeDesc = (() => {
    if (!causeCodeInputVal || !causeCodeList) return null
    const num = parseInt(
      String(causeCodeInputVal)
        .split(' - ')[0]
        .trim(),
      10
    )
    if (isNaN(num)) return null
    const match = causeCodeList.find(cc => Number(cc.causeCode) === num)
    return match ? match.description : null
  })()

  const availableHours = (() => {
    const statusRow = (unitHourlyRows || []).find(
      r => r.metric === 'Unit Status'
    )
    if (!statusRow) return HOURS
    return HOURS.filter(h => !LOCKED_STATUSES.has(statusRow[`h${h}`]))
  })()

  const causeCodeValid =
    isRS ||
    (!!causeCodeInputVal &&
      (!(causeCodeList && causeCodeList.length > 0) || causeCodeDesc != null))

  const selectedHour = form.getFieldValue('eventHour')

  const { unallocForHour, constrainingHour } = (() => {
    if (selectedHour == null || !unitHourlyRows)
      return { unallocForHour: null, constrainingHour: null }
    const unallocRow = unitHourlyRows.find(r => r.metric === 'Unallocated')
    if (!unallocRow) return { unallocForHour: null, constrainingHour: null }
    const raw = unallocRow[`h${selectedHour}`]
    if (raw == null || raw === '')
      return { unallocForHour: null, constrainingHour: null }
    const v = parseFloat(raw)
    if (isNaN(v)) return { unallocForHour: null, constrainingHour: null }
    return { unallocForHour: v, constrainingHour: selectedHour }
  })()

  const availCapExceeded = (() => {
    if (isZeroCap) return false
    const availCap = form.getFieldValue('availCap')
    if (availCap == null || unallocForHour == null) return false
    return availCap > unallocForHour
  })()

  const saveDisabled = !causeCodeValid || availCapExceeded

  // Derive availCap from derate for insert mode (placed after unallocForHour is computed)
  useEffect(() => {
    if (!useDerate) return
    const computed =
      unallocForHour != null && derate != null
        ? Math.round((unallocForHour - derate) * 100) / 100
        : undefined
    formRef.current.setFieldsValue({ availCap: computed })
  }, [derate, unallocForHour, useDerate]) // eslint-disable-line react-hooks/exhaustive-deps

  const title = `${isEdit ? 'Edit' : 'Add'} Event${
    selectedUnit ? ` — ${selectedUnit}` : ''
  }${selectedDate ? ` | ${selectedDate.format('MM/DD/YYYY')}` : ''}`

  return (
    <>
      <Modal
        visible={visible}
        title={title}
        onCancel={onCancel}
        onOk={handleSave}
        okText="Save"
        okButtonProps={{ disabled: saveDisabled, loading: saveLoading }}
        width={800}
        maskClosable={false}
        zIndex={101}
      >
        <Form layout="vertical">
          {/* Row 1: Date → Hour → Minute → Event Type */}
          <Row gutter={16}>
            <Col span={5}>
              <Form.Item label="Date">
                <Input
                  value={selectedDate ? selectedDate.format('MM/DD/YYYY') : ''}
                  disabled
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="Hour">
                {getFieldDecorator('eventHour', {
                  rules: [{ required: true, message: 'Required' }],
                })(
                  <Select placeholder="Hour" showSearch>
                    {availableHours.map(h => (
                      <Option key={h} value={h}>
                        {String(h).padStart(2, '0')}
                      </Option>
                    ))}
                  </Select>
                )}
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="Minute">
                {getFieldDecorator('eventMinute', { initialValue: 0 })(
                  <Select placeholder="Min" showSearch>
                    {MINUTES.map(m => (
                      <Option key={m} value={m}>
                        {String(m).padStart(2, '0')}
                      </Option>
                    ))}
                  </Select>
                )}
              </Form.Item>
            </Col>
            <Col span={11}>
              <Form.Item label="Event Type">
                {getFieldDecorator('eventType', {
                  rules: [{ required: true, message: 'Required' }],
                })(
                  <Select
                    placeholder="Select Event Type"
                    showSearch
                    onChange={setEventTypeValue}
                  >
                    {(eventTypes || [])
                      .filter(
                        e =>
                          DERATE_TYPES.has(e.eventType) ||
                          OUTAGE_TYPES.has(e.eventType)
                      )
                      .map(e => (
                        <Option key={e.eventType} value={e.eventType}>
                          {e.eventType} - {e.description}
                        </Option>
                      ))}
                  </Select>
                )}
              </Form.Item>
            </Col>
          </Row>

          {/* Row 2: Derate / Avail Cap */}
          <Row gutter={16}>
            {useDerate && (
              <Col span={8}>
                <Form.Item
                  label={
                    <span>
                      Derate (MW)
                      {unallocForHour != null && (
                        <span
                          style={{
                            marginLeft: 8,
                            fontWeight: 400,
                            color: '#52c41a',
                            fontSize: 12,
                          }}
                        >
                          {`Unallocated H${
                            constrainingHour != null
                              ? String(constrainingHour).padStart(2, '0')
                              : '--'
                          }: ${unallocForHour.toFixed(1)}`}
                        </span>
                      )}
                    </span>
                  }
                >
                  <InputNumber
                    value={derate}
                    onChange={setDerate}
                    style={{ width: '100%' }}
                    min={0}
                  />
                </Form.Item>
              </Col>
            )}
            <Col span={useDerate ? 8 : 12}>
              <Form.Item label="Avail Cap (MW)">
                {getFieldDecorator('availCap', {
                  rules: [
                    {
                      validator: (rule, value, callback) => {
                        if (isZeroCap || useDerate || value == null) {
                          callback()
                          return
                        }
                        const h = form.getFieldValue('eventHour')
                        if (h != null && unitHourlyRows) {
                          const unallocRow = unitHourlyRows.find(
                            r => r.metric === 'Unallocated'
                          )
                          if (unallocRow) {
                            const raw = unallocRow[`h${h}`]
                            const minUnalloc =
                              raw != null &&
                              raw !== '' &&
                              !isNaN(parseFloat(raw))
                                ? parseFloat(raw)
                                : null
                            if (minUnalloc != null && value > minUnalloc) {
                              callback(
                                new Error(
                                  `Cannot exceed Unallocated for H${String(
                                    h
                                  ).padStart(2, '0')} (${minUnalloc.toFixed(
                                    1
                                  )})`
                                )
                              )
                              return
                            }
                          }
                        }
                        callback()
                      },
                    },
                  ],
                })(
                  <InputNumber
                    style={{ width: '100%' }}
                    disabled={isZeroCap || useDerate}
                  />
                )}
              </Form.Item>
            </Col>
          </Row>

          {/* Cause Code */}
          <Form.Item label="Cause Code">
            <div style={{ display: 'flex', gap: 8 }}>
              {getFieldDecorator('causeCode', {
                rules: [{ required: true, message: 'Required' }],
              })(
                <Input
                  placeholder="Enter or select cause code"
                  disabled={isRS}
                  onChange={e =>
                    setSelectedCauseCode({
                      causeCode: e.target.value,
                      description: '',
                    })
                  }
                />
              )}
              <Button
                type="primary"
                disabled={isRS}
                onClick={() => setCauseCodeSearchVisible(true)}
              >
                Select
              </Button>
            </div>
            {causeCodeInputVal && causeCodeDesc && (
              <div style={{ marginTop: 4, color: '#1890ff', fontSize: 12 }}>
                {causeCodeDesc}
              </div>
            )}
            {causeCodeInputVal &&
              !causeCodeDesc &&
              causeCodeList &&
              causeCodeList.length > 0 && (
                <div style={{ marginTop: 4, color: '#f5222d', fontSize: 12 }}>
                  Cause code not found
                </div>
              )}
          </Form.Item>

          {/* COC */}
          <Form.Item label="Contributing Operating Condition (COC)">
            {getFieldDecorator('coc', { initialValue: '0' })(
              <Select placeholder="Select COC" allowClear showSearch>
                {(contribConditions || []).map(item => (
                  <Option key={item.code} value={item.code}>
                    {item.code} - {item.description}
                  </Option>
                ))}
              </Select>
            )}
          </Form.Item>

          {/* Description */}
          <Form.Item label="Description">
            {getFieldDecorator('description')(<TextArea rows={3} />)}
          </Form.Item>

          {saveError && saveError.length > 0 && (
            <div style={{ marginTop: 8 }}>
              {saveError.map((e, i) => (
                <Alert
                  key={i}
                  type={Number(e.severity) === 0 ? 'warning' : 'error'}
                  message={
                    <span dangerouslySetInnerHTML={{ __html: e.error }} />
                  }
                  showIcon
                  style={{ marginBottom: 4 }}
                />
              ))}
            </div>
          )}
        </Form>
      </Modal>

      {causeCodeSearchVisible && (
        <MultiCauseCodePropmt
          key="aeci7editcausecodeprompt"
          visibleDialog={causeCodeSearchVisible}
          onDialogClose={onCauseCodeDialogClose}
          onCancelAction={onCauseCodeTreeCancel}
          selectedKeys={causeCodeKeyValues}
          selectedValues={causeCodeValues}
          causeCodeList={causeCodeList}
          causeCodeValue={
            selectedCauseCode ? [selectedCauseCode.causeCode] : []
          }
          unitCodes={unitType || []}
          multiple={false}
          checkable={false}
          zIndex={1200}
          selectable
        />
      )}
    </>
  )
}

export default Form.create()(EventModal)
